import type {
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

/**
 * Everything a grid should still be showing when you come back to it.
 *
 * Deliberately the whole working set rather than just the filters: a user who
 * ticked twelve rows, narrowed to one collection and scrolled halfway down has
 * done real work, and opening one of those rows to check something should not
 * throw it away.
 */
export interface TableStateSnapshot {
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  globalFilter: string;
  rowSelection: RowSelectionState;
  columnVisibility: VisibilityState;
  expanded: ExpandedState;
  scrollTop: number;
}

/**
 * Kept in a module-level map rather than in React state, sessionStorage or the
 * URL, because of what each choice would mean:
 *
 *   - React state above the route would have to live in a provider that every
 *     app wires up, and would still be lost on the route change we care about.
 *   - sessionStorage would survive a reload, which sounds like a feature until
 *     a grid reopens a week later still filtered to something the user has long
 *     forgotten choosing. A reload is a reasonable "start clean" signal.
 *   - The URL would make selection and scroll position shareable, which they
 *     are not: row ids in a link break the moment the data moves on.
 *
 * So it lives exactly as long as the tab's navigation session.
 */
const store = new Map<string, TableStateSnapshot>();

export function readTableState(key: string): TableStateSnapshot | undefined {
  return store.get(key);
}

export function writeTableState(key: string, snapshot: TableStateSnapshot): void {
  store.set(key, snapshot);
}

export function clearTableState(key: string): void {
  store.delete(key);
}

/**
 * Forgets every grid whose section the user has left.
 *
 * A key is the route subtree the grid belongs to, so `/products` survives a
 * trip to `/products/2344` and is dropped the moment the user picks something
 * else from the menu. The boundary is checked on a path segment, so `/product`
 * is not treated as a parent of `/products`.
 */
export function pruneTableState(pathname: string): void {
  for (const key of [...store.keys()]) {
    if (pathname !== key && !pathname.startsWith(key.endsWith("/") ? key : `${key}/`)) {
      store.delete(key);
    }
  }
}

/**
 * Column order is the one thing that does outlive the tab.
 *
 * Everything above is working state — a filter, a selection, a scroll — and
 * a reload is a reasonable signal to start clean. An arrangement of columns
 * is a preference: someone who dragged the price column next to the SKU did
 * not do it for this session, and asking them to do it again after every
 * reload would make the feature not worth having. So it goes to localStorage,
 * keyed by the same route key, and comes back until they reset it.
 */
const ORDER_PREFIX = "table.columnOrder:";

export function readColumnOrder(key: string): ColumnOrderState | undefined {
  try {
    const raw = localStorage.getItem(ORDER_PREFIX + key);
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    return Array.isArray(parsed) && parsed.every((id) => typeof id === "string") ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function writeColumnOrder(key: string, order: ColumnOrderState): void {
  try {
    if (order.length === 0) localStorage.removeItem(ORDER_PREFIX + key);
    else localStorage.setItem(ORDER_PREFIX + key, JSON.stringify(order));
  } catch {
    // A preference that cannot be remembered still works for this session.
  }
}
